import axios from "axios";
import api_constants from "apis/api_constants";

export const automaticService = {
  get_device,
  get_devices,
  get_assembly,
  get_assemblies,
  get_firmware_updates,
  getSecondSetOfLabelingPdf,
  getPackagingReports,
  getDeviceHistoryRecord,
  getCalibrations,
  getCalibration,
  getAssemblyTests,
  getAssemblyTest,
  getAssemblyWorkOrders,
  getAssemblyWorkOrderStatistics,
  getDeviceWorkOrders,
  getDeviceWorkOrderStatistics,
  getDeviceWorkOrderAnalytics,
  prepareBatchDHRExport,
  getJobStatus,
  getFullReportOfDevices,
  getSummaryReportOfDevices,
};

async function getSummaryReportOfDevices({ serialNumbers }) {
  try {
    const userRaw = localStorage.getItem("user");
    const token = userRaw ? JSON.parse(userRaw)?.token : null;
    if (!token) {
      throw new Error("Missing auth token");
    }

    const requestOptions = {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: { serialNumbers },
      params: {
        clientTimezoneOffset: new Date().getTimezoneOffset(),
      },
      responseType: "blob",
    };

    const response = await axios(
      api_constants.getSummaryReportOfDevices,
      requestOptions
    );
    const blob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = window.URL.createObjectURL(blob);

    const pdfWindow = window.open(pdfUrl, "_blank");
    if (!pdfWindow) {
      throw new Error("Popup blocked. Please allow popups to open the PDF.");
    }
    pdfWindow.location.href = pdfUrl;
  } catch (error) {
    throw error;
  }
}

async function getFullReportOfDevices({ serialNumbers }) {
  try {
    const userRaw = localStorage.getItem("user");
    const token = userRaw ? JSON.parse(userRaw)?.token : null;
    if (!token) {
      throw new Error("Missing auth token");
    }

    const requestOptions = {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: {
        serialNumbers: serialNumbers,
      },
      params: {
        clientTimezoneOffset: new Date().getTimezoneOffset(),
      },
      responseType: "blob",
    };

    const response = await axios(
      api_constants.getFullReportOfDevices,
      requestOptions
    );
    const blob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = window.URL.createObjectURL(blob);
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      throw new Error("Popup blocked. Please allow popups to open the PDF.");
    }
    pdfWindow.location.href = pdfUrl;
  } catch (error) {
    throw error;
  }
}

async function get_device(deviceSerialNumber) {
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    data: JSON.stringify({
      deviceSerialNumber: deviceSerialNumber,
    }),
  };

  return await axios(api_constants.getDevice, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.device;
    });
}

async function get_devices(
  assemblyStatus,
  calibrationStatus,
  startDate,
  endDate,
  skip,
  limit,
  searchExpression,
  deviceType,
  deviceTypes,
  packagingStatus,
  workOrders,
  deviceSerialNumberList
) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginResponse.token}`,
    },
    data: JSON.stringify({
      manufacturerId: loginResponse.user._id,
      skip: skip,
      limit: limit,
      assemblyStatus: assemblyStatus,
      calibrationStatus: calibrationStatus,
      packagingStatus: packagingStatus,
      startDate: startDate,
      endDate: endDate,
      searchExpression: searchExpression,
      //deviceType is deprecated filter, it will be deleted when new dashboard is released
      deviceType: deviceType,
      deviceTypes: deviceTypes,
      workOrderIds: workOrders,
      deviceSerialNumberList,
    }),
  };

  return await axios(api_constants.getDevices, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return {
        devices: data?.devices,
        totalDeviceCount: data?.totalDeviceCount,
      };
    });
}

async function get_assembly(pcbSerialNumber) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginResponse.token}`,
    },
    data: JSON.stringify({
      manufacturerId: loginResponse.user._id,
      pcbSerialNumber: pcbSerialNumber,
    }),
  };

  return await axios(api_constants.getAssembly, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.assembly;
    });
}

async function get_assemblies(
  assemblyStatus,
  oadUpdateStatus,
  startDate,
  endDate,
  skip,
  limit,
  searchExpression,
  pcbType,
  pcbaTypes,
  workOrders
) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginResponse.token}`,
    },
    data: JSON.stringify({
      manufacturerId: loginResponse.user._id,
      skip: skip,
      limit: limit,
      assemblyStatus: assemblyStatus,
      oadUpdateStatus: oadUpdateStatus,
      startDate: startDate,
      endDate: endDate,
      searchExpression: searchExpression,
      //pcbType is deprecated filter, it will be deleted when new dashboard is released
      pcbType: pcbType,
      pcbaTypes: pcbaTypes,
      workOrderIds: workOrders,
    }),
  };

  return await axios(api_constants.getAssemblies, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return {
        assemblies: data.assemblies,
        totalAssemblyCount: data?.totalAssemblyCount,
      };
    });
}

async function get_firmware_updates(skip, limit) {
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    data: JSON.stringify({
      skip: skip,
      limit: limit,
    }),
  };

  return await axios(api_constants.getFirmwareUpdates, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.updates;
    });
}

async function getSecondSetOfLabelingPdf(deviceSerialNumber) {
  const requestUrl = `${api_constants.getSecondSetOfLabelingPdf}/${deviceSerialNumber}.pdf`;
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    responseType: "arraybuffer",
    responseEncoding: "binary",
  };

  return await axios(requestUrl, requestOptions)
    .then(handleSecondSetOfLabelingResponse, (error) => Promise.reject(error))
    .then((data) => {
      return Buffer.from(data).toString("base64");
    });
}

async function getPackagingReports(deviceId) {
  const requestUrl = api_constants.getPackagingReports.replace(
    ":deviceId",
    deviceId
  );
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.packagingReports;
    });
}

async function getDeviceHistoryRecord(deviceId) {
  const requestUrl = api_constants.getDeviceHistoryRecord.replace(
    ":deviceId",
    deviceId
  );
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    params: {
      clientTimezoneOffset: new Date().getTimezoneOffset(),
    }
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data;
    });
}

async function getAssemblyTests(assemblyId, skip, limit) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getAssemblyTests.replace(
    ":assemblyId",
    assemblyId
  );
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginResponse.token}`,
    },
    params: {
      manufacturerId: loginResponse.user._id,
      skip: skip,
      limit: limit,
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.assemblyTests;
    });
}

async function getAssemblyTest(assemblyId, assemblyTestId) {
  const requestUrl = api_constants.getAssemblyTest
    .replace(":assemblyId", assemblyId)
    .replace(":assemblyTestId", assemblyTestId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.assemblyTest;
    });
}
async function getCalibrations(deviceId, skip, limit) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getCalibrations.replace(
    ":deviceId",
    deviceId
  );
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginResponse.token}`,
    },
    params: {
      manufacturerId: loginResponse.user._id,
      skip: skip,
      limit: limit,
    },
  };
  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.calibrations;
    });
}

async function getCalibration(deviceId, calibrationId) {
  const requestUrl = api_constants.getCalibration
    .replace(":deviceId", deviceId)
    .replace(":calibrationId", calibrationId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.calibration;
    });
}


async function getAssemblyWorkOrders(startDate, endDate, productType, skip, limit, searchExp) {
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    params: {
      startDate: startDate,
      endDate: endDate,
      productType: productType,
      skip: skip,
      limit: limit,
      searchExpression: searchExp,
    }
  };

  return await axios(api_constants.getAssemblyWorkOrders, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.workOrders;
    });
}

async function getAssemblyWorkOrderStatistics(workOrderId) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getAssemblyWorkOrderStatistics.replace(":workOrderId", workOrderId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${loginResponse.token}`,
    },
    params: {
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.statisticsData;
    });
}

async function getDeviceWorkOrders(startDate, endDate, productType, skip, limit, searchExp) {
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${JSON.parse(localStorage.getItem("user")).token}`,
    },
    params: {
      startDate: startDate,
      endDate: endDate,
      productType: productType,
      skip: skip,
      limit: limit,
      searchExpression: searchExp,
    }
  };

  return await axios(api_constants.getDeviceWorkOrders, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.workOrders;
    });
}

async function getDeviceWorkOrderStatistics(workOrderId) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getDeviceWorkOrderStatistics.replace(":workOrderId", workOrderId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${loginResponse.token}`,
    },
    params: {
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.statisticsData;
    });
}

async function getDeviceWorkOrderAnalytics(workOrderId, skip, limit) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getDeviceWorkOrderAnalytics.replace(":workOrderId", workOrderId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${loginResponse.token}`,
    },
    params: {
      skip: skip,
      limit: limit,
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.analyticsData;
    });
}

async function prepareBatchDHRExport(workOrderId) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.prepareBatchDHRExport.replace(":workOrderId", workOrderId);
  const requestOptions = {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${loginResponse.token}`,
    },
    params: {
    },
  };

  return await axios(requestUrl, requestOptions)
    .then(handleResponse, (error) => Promise.reject(error))
    .then((data) => {
      return data.jobInfo;
    });
}
async function getJobStatus(jobId) {
  const loginResponse = JSON.parse(localStorage.getItem("user"));
  const requestUrl = api_constants.getJobStatus.replace(":jobId", jobId);
  const requestOptions = {
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${loginResponse.token}`,
    },
    params: {
    },
  };

  return await axios(requestUrl, requestOptions)
    .then((response) => (response.data));
}


function handleResponse(response) {
  const data = response.data;
  if (!data.result.success) {
    const error = data.result;
    return Promise.reject(error);
  }
  return data;
}

function handleSecondSetOfLabelingResponse(response) {
  const buffer = response.data;
  const text = new TextDecoder().decode(buffer);
  try {
    const json = JSON.parse(text);
     if (json?.result?.success === false) {
      return Promise.reject(new Error(json?.result?.description));
    }
  } catch {
    // not json → pdf
  }
  return buffer;
}
